import { SchemaFormDefinition, SchemaFormFields } from '@mews-commander/form';
import { lt } from '@mews/framework';
import { Stack, Typography } from '@optimus-web/core';
import { PriceAdjustmentDiscriminator, PriceAdjustmentFieldNames, servicesProductSchema } from '../schemas';
import { RepricingGroup } from '../RepricingGroup';
import type { EditServicesProductFormDefinitionParams } from '../types';
import { FixedValueField } from './FixedValueField';
import { PercentageValueField } from './PercentageValueField';
import { AdjustmentTypeRadioGroupField } from './AdjustmentTypeRadioGroupField';

export const editServicesProductFormDefinition = ({
    basePriceValue,
    currencySymbol,
    localizedCurrency,
    chargingOptions,
    productOptions,
}: EditServicesProductFormDefinitionParams): SchemaFormDefinition<typeof servicesProductSchema> => ({
    fields: {
        charging: {
            label: lt('ConfigureProductServicesPaymentsCharging'),
            field: {
                type: 'select',
                options: chargingOptions.options,
                disableInputSearch: true,
            },
            disabled: true,
        },
        consumptionMoment: {
            label: lt('ConfigureProductServicesPaymentsTiming'),
            field: {
                type: 'select',
                options: chargingOptions.subOptions ?? [],
                disableInputSearch: true,
            },
            required: true,
        },
        options: {
            label: lt('ProductOptions'),
            field: {
                type: 'enum',
                enumPrefix: 'ProductOptions',
                omitHint: true,
                enabledValues: productOptions,
            },
        },
        promotions: {
            label: lt('ConfigureProductServicesPromotions'),
            field: {
                type: 'enum',
                enumPrefix: 'Promotions',
                omitHint: true,
            },
        },
        priceAdjustment: {
            label: lt('ConfigureProductServicesFieldAdjustmentType'),
            render: (props, meta) => (
                <SchemaFormFields.DiscriminatedUnion
                    namePrefix={props.name}
                    schema={meta.schema}
                    formDefinition={{
                        fields: {
                            discriminator: {
                                label: lt('ConfigureProductServicesFieldAdjustmentType'),
                                render: ({ name, label }) => (
                                    <AdjustmentTypeRadioGroupField label={label} name={name} />
                                ),
                            },
                            fixedValue: {
                                label: lt('GeneralAdjustedPriceTaxExcl'),
                                required: true,
                                render: (innerProps) => (
                                    <FixedValueField
                                        name={innerProps.name}
                                        basePriceValue={basePriceValue}
                                        currencySymbol={currencySymbol}
                                        localizedCurrency={localizedCurrency}
                                    />
                                ),
                            },
                            percentageValue: {
                                label: lt('GeneralAdjustedPriceTaxExcl'),
                                render: (innerProps) => <PercentageValueField name={innerProps.name} />,
                            },
                        },
                        renderFields: {
                            [PriceAdjustmentDiscriminator.Fixed]: [
                                PriceAdjustmentFieldNames.Discriminator,
                                PriceAdjustmentFieldNames.FixedValue,
                            ],
                            [PriceAdjustmentDiscriminator.Percentage]: [
                                PriceAdjustmentFieldNames.Discriminator,
                                PriceAdjustmentFieldNames.PercentageValue,
                            ],
                            [PriceAdjustmentDiscriminator.None]: [PriceAdjustmentFieldNames.Discriminator],
                        },
                    }}
                />
            ),
        },
        repriceExistingReservations: {
            render: () => <RepricingGroup />,
        },
    },
    renderFields: ({
        options,
        promotions,
        repriceExistingReservations,
        priceAdjustment,
        charging,
        consumptionMoment,
    }) => {
        return (
            <Stack vertical spacing="400">
                <Stack spacing="100" distribution="stretch">
                    {charging}
                    {consumptionMoment}
                </Stack>
                <Stack vertical spacing="300">
                    <Typography textStyle="bodyLargeStrong" as="div">
                        {lt('ConfigureProductServicesSubHeadingAvailability')}
                    </Typography>
                    {options}
                    {promotions}
                </Stack>
                {priceAdjustment}
                {repriceExistingReservations}
            </Stack>
        );
    },
});
